/**
 * Fluevog size picker behaviour.
 *
 * Loaded on any page that can show the size picker: the PDP (via the
 * fluevog-size-picker block) and collection/search pages (via the quick-add
 * modal). Uses delegated handlers so the WOMEN / MEN toggle and the mobile
 * dropdowns work even for pickers injected after page load (quick-add modal).
 *
 * Desktop uses the hand-drawn size circles (radio inputs). On mobile those
 * circles are hidden and custom dropdown menus (gender + size) are shown
 * instead — native <select> pickers were opening in unpredictable positions.
 * The radios stay the canonical variant inputs; the menus proxy to them so
 * the theme's variant logic (price, URL, availability) keeps working.
 */
(function () {
  if (window.__fvSizePickerInit) return;
  window.__fvSizePickerInit = true;

  // On unisex products variant sizes are stored as the men's value. A women's
  // size is 2 larger than the equivalent men's size, so the women's label is the
  // men's value plus 2. (Single-gender products have no toggle to convert.)
  var WOMEN_OFFSET = 2;

  // The gender the shopper was browsing in on the collection page, so a unisex
  // PDP opens on the same numbering they just filtered by rather than always
  // defaulting to women's. Written by assets/fluevog-collection-toolbar.js under
  // the same key; sessionStorage throws in Safari private mode, so it's guarded.
  var GENDER_STORAGE_KEY = 'fv:size-gender';

  function readStoredGender() {
    try {
      var stored = sessionStorage.getItem(GENDER_STORAGE_KEY);
      return stored === 'women' || stored === 'men' ? stored : null;
    } catch (error) {
      return null;
    }
  }

  function writeStoredGender(gender) {
    try {
      sessionStorage.setItem(GENDER_STORAGE_KEY, gender);
    } catch (error) {
      // Storage unavailable — the choice just doesn't carry to the next page.
    }
  }

  function convert(base, offset) {
    var num = parseFloat(base);
    if (isNaN(num)) return null;
    var val = Math.round((num + offset) * 10) / 10;
    return val.toString();
  }

  function closeDropdown(field) {
    if (!field) return;
    field.classList.remove('is-open');
    var trigger = field.querySelector('[data-fv-dropdown-trigger]');
    var menu = field.querySelector('[data-fv-dropdown-menu]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (menu) menu.hidden = true;
  }

  function closeAllDropdowns(except) {
    document.querySelectorAll('[data-fv-dropdown].is-open').forEach(function (field) {
      if (field !== except) closeDropdown(field);
    });
  }

  function openDropdown(field) {
    if (!field) return;
    closeAllDropdowns(field);
    field.classList.add('is-open');
    var trigger = field.querySelector('[data-fv-dropdown-trigger]');
    var menu = field.querySelector('[data-fv-dropdown-menu]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (menu) menu.hidden = false;
  }

  function toggleDropdown(field) {
    if (!field) return;
    if (field.classList.contains('is-open')) {
      closeDropdown(field);
    } else {
      openDropdown(field);
    }
  }

  // Relabel both the circle buttons and the mobile size menu for the active
  // gender. The underlying value/variant never changes — only the label.
  // Gift card "sizes" are prices, so never convert those labels.
  function applyOffset(picker, offset) {
    if (picker.hasAttribute('data-fv-gift-card')) return;

    picker.querySelectorAll('.variant-option__button-label__text').forEach(function (el) {
      if (!el.dataset.fvBaseSize) {
        el.dataset.fvBaseSize = el.textContent.trim();
      }
      var next = convert(el.dataset.fvBaseSize, offset);
      if (next !== null) el.textContent = next;
    });

    picker.querySelectorAll('[data-fv-size-option]').forEach(function (btn) {
      var base = btn.getAttribute('data-fv-base');
      var label = btn.querySelector('[data-fv-size-option-label]') || btn;
      var next = convert(base, offset);
      if (next !== null) label.textContent = next;
    });

    // Keep the closed trigger label in sync with the selected size option.
    var selected = picker.querySelector('[data-fv-size-option].is-selected [data-fv-size-option-label]');
    var sizeTriggerLabel = picker.querySelector('[data-fv-size-trigger] [data-fv-dropdown-label]');
    if (selected && sizeTriggerLabel) {
      sizeTriggerLabel.textContent = selected.textContent.trim();
    }
  }

  // Single-gender pickers render no [data-gender] controls at all, so they fall
  // through to offset 0 — correct, because a single-gender variant already holds
  // that gender's own size.
  function currentOffset(picker) {
    var womenBtn = picker.querySelector('.fv-size-picker__gender[data-gender="women"]');
    var womenOption = picker.querySelector('[data-fv-gender-option][data-gender="women"]');
    var womenActive =
      (womenBtn && womenBtn.classList.contains('is-active')) ||
      (womenOption && womenOption.classList.contains('is-selected'));
    return womenActive ? WOMEN_OFFSET : 0;
  }

  // The size currently selected, as stored on the variant (the men's value on
  // unisex products). Read from the radio's `value` (the raw option value) and
  // the mobile option's data-fv-base — never from the visible label text, which
  // applyOffset may already have converted to women's, and would double-convert
  // here.
  function selectedBaseSize(picker) {
    var radios = picker.querySelectorAll('.variant-picker input[type="radio"]:checked');
    for (var i = 0; i < radios.length; i++) {
      var fieldset = radios[i].closest('fieldset');
      var legend = fieldset && fieldset.querySelector('legend');
      var optionName = (legend ? legend.textContent : radios[i].name || '').toLowerCase();
      if (optionName.indexOf('size') !== -1) return radios[i].value;
    }

    var mobileSelected = picker.querySelector('[data-fv-size-option].is-selected');
    return mobileSelected ? mobileSelected.getAttribute('data-fv-base') : null;
  }

  // Keep the "M 10 / W 12" line item property in step with the selected size.
  // The server-rendered value goes stale on an in-page size change because
  // variant-picker.js morphs only the <variant-picker> element — the
  // buy-buttons block is never re-rendered (product-form.js hand-updates its own
  // input[name="id"] for the same reason).
  //
  // The picker block and the buy-buttons block are siblings, not nested, so the
  // input is looked up from the surrounding section / quick-add modal. A missing
  // input just means the property doesn't apply here (no gender metafield, gift
  // card, quick-add before injection) — no-op.
  function syncSizeFitProperty(picker) {
    if (!picker || picker.hasAttribute('data-fv-gift-card')) return;

    var scope = picker.closest('.shopify-section, .quick-add-modal__content') || document;
    var input = scope.querySelector('form[data-type="add-to-cart-form"] [data-fv-size-fit]');
    if (!input) return;

    // Couldn't tell which size is selected — leave the server-rendered value alone
    // rather than guessing.
    var base = selectedBaseSize(picker);
    if (base === null) return;

    // fluevog-size-fit-property.liquid stamps which fits the product is sold in.
    // Only unisex stores the men's size and needs the women's conversion; a
    // single-gender variant already holds that gender's own size.
    var mode = input.getAttribute('data-fv-size-fit') || 'unisex';

    // convert() already formats the way fluevog-size-fit-property.liquid does
    // (no trailing .0, half sizes kept). Keep the two in step.
    var size = convert(base, 0);

    // Same bail-out as the Liquid snippet: a non-numeric or too-small size has no
    // fit to spell out. Blanking the value drops the property server-side, which
    // beats submitting "M 0 / W 2".
    if (size === null || parseFloat(size) <= 0) {
      input.value = '';
      return;
    }

    if (mode === 'men' || mode === 'women') {
      input.value = (mode === 'men' ? 'M ' : 'W ') + size;
      return;
    }

    // convert() can't fail here — `size` above already parsed the same base —
    // and a positive men's size always converts to a positive women's one.
    input.value = 'M ' + size + ' / W ' + convert(base, WOMEN_OFFSET);
  }

  // Set the active gender from any control (toggle button or dropdown) and keep
  // every gender control in the picker in sync, then relabel the sizes.
  function setGender(picker, gender) {
    if (!picker || !gender) return;

    var group = picker.querySelector('.fv-size-picker__genders');
    if (group) {
      group.querySelectorAll('.fv-size-picker__gender').forEach(function (b) {
        var active = b.getAttribute('data-gender') === gender;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    picker.querySelectorAll('[data-fv-gender-option]').forEach(function (btn) {
      var active = btn.getAttribute('data-gender') === gender;
      btn.classList.toggle('is-selected', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active) {
        var label = picker.querySelector('[data-fv-gender-trigger] [data-fv-dropdown-label]');
        if (label) label.textContent = btn.textContent.trim();
      }
    });

    applyOffset(picker, gender === 'women' ? WOMEN_OFFSET : 0);
  }

  // Sort the size buttons ascending by their numeric value. Non-numeric values
  // keep their relative order and are placed after numeric ones.
  function sortSizes(picker) {
    picker.querySelectorAll('.variant-option--buttons').forEach(function (fieldset) {
      if (fieldset.dataset.fvSorted === 'true') return;
      fieldset.dataset.fvSorted = 'true';

      var labels = Array.prototype.slice.call(fieldset.querySelectorAll('.variant-option__button-label'));
      if (labels.length < 2) return;

      labels
        .map(function (label, index) {
          var textEl = label.querySelector('.variant-option__button-label__text');
          var raw = textEl ? textEl.textContent.trim() : label.textContent.trim();
          var num = parseFloat(raw);
          return { label: label, num: isNaN(num) ? Infinity : num, index: index };
        })
        .sort(function (a, b) {
          return a.num - b.num || a.index - b.index;
        })
        .forEach(function (item) {
          fieldset.appendChild(item.label);
        });
    });
  }

  // Same ascending sort for the mobile size menu options.
  function sortSizeMenu(picker) {
    picker.querySelectorAll('[data-fv-size-menu]').forEach(function (menu) {
      if (menu.dataset.fvSorted === 'true') return;
      menu.dataset.fvSorted = 'true';

      var items = Array.prototype.slice.call(menu.children);
      if (items.length < 2) return;

      items
        .map(function (li, index) {
          var btn = li.querySelector('[data-fv-size-option]');
          var num = btn ? parseFloat(btn.getAttribute('data-fv-base')) : NaN;
          return { li: li, num: isNaN(num) ? Infinity : num, index: index };
        })
        .sort(function (a, b) {
          return a.num - b.num || a.index - b.index;
        })
        .forEach(function (item) {
          menu.appendChild(item.li);
        });
    });
  }

  // Drive the canonical radio input from the mobile size menu so the theme's
  // variant-picker component runs its usual update (price, URL, availability).
  function selectSize(picker, optionBtn) {
    if (!picker || !optionBtn) return;

    picker.querySelectorAll('[data-fv-size-option]').forEach(function (btn) {
      var active = btn === optionBtn;
      btn.classList.toggle('is-selected', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    var labelText = (optionBtn.querySelector('[data-fv-size-option-label]') || optionBtn).textContent.trim();
    var triggerLabel = picker.querySelector('[data-fv-size-trigger] [data-fv-dropdown-label]');
    if (triggerLabel) triggerLabel.textContent = labelText;

    var radio = null;
    var optionValueId = optionBtn.getAttribute('data-option-value-id');
    if (optionValueId) {
      radio = picker.querySelector('.variant-picker input[data-option-value-id="' + optionValueId + '"]');
    }
    if (!radio) {
      var value = optionBtn.getAttribute('data-value');
      radio = Array.prototype.slice
        .call(picker.querySelectorAll('.variant-picker input[type="radio"]'))
        .filter(function (r) {
          return r.value === value;
        })[0];
    }

    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncSizeFitProperty(picker);
  }

  function initPicker(picker) {
    sortSizes(picker);
    sortSizeMenu(picker);

    // Adopt the browsing gender only where the picker actually offers both — a
    // single-gender product has no toggle and its sizes are already its own.
    // Storage always holds the shopper's latest explicit choice, so re-adopting
    // on every init (variant:update morphs, quick-add injection) can't stomp it.
    var stored = readStoredGender();
    var hasGenderToggle =
      picker.querySelector('.fv-size-picker__gender[data-gender="men"]') &&
      picker.querySelector('.fv-size-picker__gender[data-gender="women"]');

    if (stored && hasGenderToggle) {
      setGender(picker, stored);
    } else {
      applyOffset(picker, currentOffset(picker));
    }

    syncSizeFitProperty(picker);
  }

  function initAll(root) {
    (root || document).querySelectorAll('.fv-size-picker').forEach(initPicker);
  }

  // Delegated clicks: desktop gender tabs, mobile dropdown open/close + options.
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    // Desktop WOMEN / MEN text toggle.
    var genderTab = target.closest('.fv-size-picker__gender');
    if (genderTab) {
      var tabPicker = genderTab.closest('.fv-size-picker');
      var tabGender = genderTab.getAttribute('data-gender');
      setGender(tabPicker, tabGender);
      // Written here rather than inside setGender so the adopt path stays a
      // pure read and can't echo a stale value back into storage.
      if (tabGender === 'women' || tabGender === 'men') writeStoredGender(tabGender);
      return;
    }

    // Mobile gender option inside the menu.
    var genderOption = target.closest('[data-fv-gender-option]');
    if (genderOption) {
      var genderPicker = genderOption.closest('.fv-size-picker');
      var optionGender = genderOption.getAttribute('data-gender');
      setGender(genderPicker, optionGender);
      if (optionGender === 'women' || optionGender === 'men') writeStoredGender(optionGender);
      closeDropdown(genderOption.closest('[data-fv-dropdown]'));
      return;
    }

    // Mobile size option inside the menu.
    var sizeOption = target.closest('[data-fv-size-option]');
    if (sizeOption) {
      var sizePicker = sizeOption.closest('.fv-size-picker');
      selectSize(sizePicker, sizeOption);
      closeDropdown(sizeOption.closest('[data-fv-dropdown]'));
      return;
    }

    // Toggle a mobile dropdown open/closed.
    var trigger = target.closest('[data-fv-dropdown-trigger]');
    if (trigger) {
      event.preventDefault();
      toggleDropdown(trigger.closest('[data-fv-dropdown]'));
      return;
    }

    // Click outside closes any open menu.
    if (!target.closest('[data-fv-dropdown]')) {
      closeAllDropdowns();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAllDropdowns();
  });

  // Desktop size circles: the property has to follow the selection immediately,
  // before the theme's own variant fetch comes back.
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var picker = target.closest('.fv-size-picker');
    if (picker) syncSizeFitProperty(picker);
  });

  // Keep the low-stock message in sync with the selected size on the PDP. The
  // theme's variant:update event carries the freshly server-rendered section
  // HTML, so we swap in the message computed for the newly selected variant.
  document.addEventListener('variant:update', function (event) {
    var html = event && event.detail && event.detail.data && event.detail.data.html;
    if (html) {
      document.querySelectorAll('.fv-size-picker [data-fv-lowstock]').forEach(function (current) {
        var blockId = current.getAttribute('data-block-id');
        var next = html.querySelector('[data-fv-lowstock][data-block-id="' + blockId + '"]');
        if (next) current.replaceWith(next);
      });
    }

    // A morph can re-render the picker (e.g. combined-listing colour change),
    // resetting labels/sort flags — re-initialise so the dropdowns stay correct.
    closeAllDropdowns();
    initAll(document);
  });

  // Re-initialise when new pickers are injected (quick-add modal dispatches this).
  document.addEventListener('fv:size-picker:init', function () {
    initAll(document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
